import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { styled } from "@mui/system";

import moment from "moment";
import {
  useLazyGetChatHistoryQuery,
  useLazyGetChatListQuery,
} from "../services/main";
import useAuth from "../hooks/useAuth";
import { errorToast } from "../helpers";
import { connectChatSocket } from "../utils/socket";
import useTranslation from "../hooks/Translation";

// Styled components
const Root = styled("div")({
  display: "flex",
  height: "100vh",
});

const Inbox = styled(Card)({
  width: "310px",
  minWidth: "310px",
  borderRadius: 5,
  display: "flex",
  flexDirection: "column",
});

const InboxHeader = styled("div")({
  padding: "16px",
  backgroundColor: "#e0e0e0",
  borderRadius: 5,
});

const InboxContent = styled("div")({
  flexGrow: 1,
  overflowY: "auto",
  padding: "8px",
});

const ConversationButton = styled(Button)({
  justifyContent: "space-between",
  textTransform: "none",
  padding: "8px",
  width: "100%",
});

const MessageArea = styled("div")({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
});

const MessageList = styled("div")({
  flexGrow: 1,
  overflowY: "auto",
  padding: "16px",
});

const MessageInput = styled("div")({
  display: "flex",
  alignItems: "center",
  padding: "16px",
});

let socket = null as any as WebSocket;

export default function QuoteChat({ sessionId }: any) {
  const user = useAuth();
  const t = useTranslation();
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [chatList] = useLazyGetChatListQuery();
  const [chatHistory] = useLazyGetChatHistoryQuery();
  const [chatListData, setChatListData] = useState<any>([]);
  const [chatHistoryData, setChatHistoryData] = useState<any>([]);
  const [message, setMessage] = useState("");
  const [selectedId, setSelectedId] = useState("");

  const scrollToSection = () => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  //getting chat list
  const getAllChats = async () => {
    try {
      const response = await chatList({}).unwrap();
      if (response?.status === 200) {
        setChatListData(response?.data);
      } else {
        setChatListData([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  //getting chat history
  const getChatHistory = async (sessionId: any) => {
    try {
      const response = await chatHistory(sessionId).unwrap();
      if (response?.status === 200) {
        setChatHistoryData(response?.data);
      } else {
        setChatHistoryData([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  //message listener
  const listenToMessage = () => {
    socket?.addEventListener("message", (event) => {
      try {
        const parsedData = JSON.parse(event.data); // Parse the string to a JavaScript object
        setChatHistoryData((prev: any) => [...prev, parsedData]);
        setTimeout(() => {
          scrollToSection();
        }, 500);
      } catch (error) {
        console.error("Failed to parse message data:", error);
        console.log("Raw message data:", event.data); // Keep a log of the raw data
      }
    });
  };

  //send message
  const sendMessage = async () => {
    setMessage("");
    socket?.send(
      JSON.stringify({
        type: "chat",
        message: message,
      })
    );
    setTimeout(() => {
      scrollToSection();
    }, 500);
  };

  useEffect(() => {
    getAllChats();
  }, []);

  useEffect(() => {
    setSelectedId(sessionId);
    setTimeout(() => {
      scrollToSection();
    }, 500);

    socket = connectChatSocket(user?.id || "", sessionId || "");
    if (socket) {
      listenToMessage();
    }
    getChatHistory(sessionId);
  }, []);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevents adding a new line
      if (message.trim().length > 0) {
        sendMessage();
      } else {
        errorToast(t.error.pleaseWrite);
      }
    }
  };

  return (
    <Root sx={{ maxHeight: 450, marginTop: 2 }}>
      {/* Right side: Message Area */}
      <MessageArea>
        <MessageList>
          {/* Sample messages */}
          {chatHistoryData?.map((item: any) => {
            return (
              <div key={item?.id}>
                {item?.sender === user?.id ? (
                  <Card
                    sx={{
                      width: "fit-content",
                      maxWidth: "70%",
                      marginLeft: "auto",
                      backgroundColor: "#2B604C",
                      color: "white",
                      marginBottom: 2,
                      textAlign: "left",
                      padding: 1,
                      borderRadius: 2,
                      borderTopRightRadius: 0,
                    }}
                  >
                    <CardContent sx={{ paddingBottom: 0 }}>
                      <Typography variant="body2" color="white">
                        {item?.user_input || "-"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 11, textAlign: "end" }}
                        color="white"
                      >
                        {moment(item.timestamp).format("h:mm A") || "-"}
                      </Typography>
                    </CardContent>
                  </Card>
                ) : (
                  <Card
                    sx={{
                      maxWidth: "70%",
                      width: "fit-content",
                      marginBottom: 2,
                      padding: 1,
                      borderRadius: 2,
                      borderTopLeftRadius: 0,
                    }}
                  >
                    <CardContent>
                      <Typography variant="body2">
                        {item?.user_input || "-"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 11, textAlign: "end" }}
                      >
                        {moment(item.time_stamp).format("h:mm A") || "-"}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })}
          <div ref={sectionRef} />
        </MessageList>

        <MessageInput>
          <TextField
            fullWidth
            placeholder={t.main.typeMessage}
            variant="outlined"
            size="small"
            type="text"
            value={message}
            onKeyDown={handleKeyPress}
            onChange={(val: any) => setMessage(val.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#2B604C", // Default border color
                },
                "&:hover fieldset": {
                  borderColor: "#2B604C", // Border color on hover
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#2B604C", // Border color when focused
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "black", // Placeholder color
                },
              },
            }}
          />
          <Button
            sx={{ marginLeft: 2 }}
            variant="outlined"
            onClick={() => {
              if (message?.length) {
                sendMessage();
              } else {
                errorToast(t.error.pleaseWrite);
              }
            }}
          >
            <Send />
          </Button>
        </MessageInput>
      </MessageArea>
    </Root>
  );
}
